import { Fragment, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Printer, MessageCircle, PenLine, ChevronDown, Send, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fmtDate, fmtPhone } from '@/lib/exporters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS = {
  inactive: { label: 'Inativa', style: 'bg-red-100 text-red-700', dot: 'bg-red-500', rank: 0 },
  low: { label: 'Baixa ativação', style: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', rank: 1 },
  new: { label: 'Nova', style: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', rank: 2 },
  active: { label: 'Ativa', style: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', rank: 3 },
};

const AdminActivationTab = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [logs, setLogs] = useState({});
  const [customOpen, setCustomOpen] = useState(false);
  const [customText, setCustomText] = useState(
    'Oi [nome do dono]! Passando para saber como está a [nome da empresa] com o Falo Com O Dono.'
  );
  const [sending, setSending] = useState(false);
  const [f, setF] = useState({ activation: '', plan: '', days: '', silence: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/registry/companies`, auth());
      setItems(res.data.items);
    } catch { toast.error('Erro ao carregar empresas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const daysWithoutReview = (c) => {
    if (!c.last_feedback_at) return c.days_since;
    return Math.floor((Date.now() - new Date(c.last_feedback_at).getTime()) / 86400000);
  };

  const filtered = items.filter(c => {
    if (f.activation === 'inactive' && c.activation !== 'inactive') return false;
    if (f.activation === 'low' && c.activation !== 'low') return false;
    if (f.activation === 'until3' && c.total_feedbacks > 3) return false;
    if (f.plan && c.plan !== f.plan) return false;
    if (f.days && c.days_since > Number(f.days)) return false;
    if (f.silence && daysWithoutReview(c) < Number(f.silence)) return false;
    return true;
  }).sort((a, b) => STATUS[a.activation].rank - STATUS[b.activation].rank
    || a.total_feedbacks - b.total_feedbacks);

  const alertCount = items.filter(c => c.total_feedbacks === 0 && c.days_since >= 7).length;

  const total = items.length || 1;
  const pct = (n) => Math.round((n / total) * 100);
  const counts = {
    inactive: items.filter(c => c.activation === 'inactive').length,
    low: items.filter(c => c.activation === 'low').length,
    active: items.filter(c => c.activation === 'active').length,
    new: items.filter(c => c.activation === 'new').length,
  };

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(i => i !== id) : [...s, id]);
  const toggleAll = () => setSelected(s => s.length === filtered.length ? [] : filtered.map(c => c.id));

  const send = async (action_type, message_text = '') => {
    if (!selected.length) return toast.error('Selecione pelo menos uma empresa');
    const nomes = items.filter(c => selected.includes(c.id)).map(c => c.name);
    const label = action_type === 'resend_plaques' ? 'as plaquinhas'
      : action_type === 'activation_message' ? 'a mensagem de ativação' : 'a mensagem personalizada';
    if (!window.confirm(`Enviar ${label} por WhatsApp para ${selected.length} empresa(s)?\n\n${nomes.slice(0, 8).join('\n')}${nomes.length > 8 ? `\n... e mais ${nomes.length - 8}` : ''}`)) return;

    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/activation/send`,
        { action_type, company_ids: selected, message_text }, auth());
      const falhas = res.data.results.filter(r => !r.sent);
      toast.success(`Enviado para ${res.data.sent} de ${res.data.total} empresa(s)`);
      if (falhas.length) {
        toast.error(`${falhas.length} não receberam: ${falhas.slice(0, 3).map(r => `${r.company_name} (${r.reason})`).join(' · ')}`,
          { duration: 9000 });
      }
      setSelected([]);
      setCustomOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar'); }
    finally { setSending(false); }
  };

  const openLogs = async (companyId) => {
    setExpanded(expanded === companyId ? null : companyId);
    if (!logs[companyId]) {
      const res = await axios.get(`${API}/admin/activation/logs`, { ...auth(), params: { company_id: companyId } });
      setLogs(l => ({ ...l, [companyId]: res.data.items }));
    }
  };

  const plans = [...new Set(items.map(c => c.plan))];

  return (
    <div className="space-y-4" data-testid="admin-activation-tab">
      {alertCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3" data-testid="inactivity-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800 flex-1">
            <strong>{alertCount} empresa(s)</strong> já passaram de 7 dias desde o cadastro e ainda não receberam nenhuma avaliação.
          </p>
          <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100"
            onClick={() => setF({ activation: 'inactive', plan: '', days: '', silence: '7' })}
            data-testid="inactivity-alert-filter">
            Ver essas empresas
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {['inactive', 'low', 'active', 'new'].map(k => (
          <div key={k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`count-${k}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2.5 h-2.5 rounded-full ${STATUS[k].dot}`} />
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">{STATUS[k].label}</p>
            </div>
            <p className="text-2xl font-bold text-[#0A2647]">{counts[k]} <span className="text-sm font-normal text-slate-400">({pct(counts[k])}%)</span></p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <select value={f.activation} onChange={e => setF({ ...f, activation: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="activation-filter">
            <option value="">Todas as empresas</option>
            <option value="inactive">Só inativas (0 avaliações)</option>
            <option value="low">Só baixa ativação (1 a 2)</option>
            <option value="until3">Até 3 avaliações</option>
          </select>
          <select value={f.plan} onChange={e => setF({ ...f, plan: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="activation-filter-plan">
            <option value="">Todos os planos</option>
            {plans.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={f.days} onChange={e => setF({ ...f, days: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="activation-filter-days">
            <option value="">Cadastro: qualquer data</option>
            <option value="7">Últimos 7 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="90">Últimos 90 dias</option>
          </select>
          <select value={f.silence} onChange={e => setF({ ...f, silence: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="activation-filter-silence">
            <option value="">Sem avaliação há: qualquer tempo</option>
            <option value="7">7 dias ou mais</option>
            <option value="15">15 dias ou mais</option>
            <option value="30">30 dias ou mais</option>
            <option value="60">60 dias ou mais</option>
          </select>
          <div className="flex items-center text-sm text-slate-500" data-testid="activation-selected-count">
            {selected.length} selecionada(s) de {filtered.length}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
          <Button onClick={() => send('resend_plaques')} disabled={sending || !selected.length}
            size="sm" className="bg-[#0A2647] hover:bg-[#0e3260] gap-2 mt-3" data-testid="resend-plaques-btn">
            <Printer className="w-4 h-4" /> Reenviar Plaquinhas
          </Button>
          <Button onClick={() => send('activation_message')} disabled={sending || !selected.length}
            size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2 mt-3" data-testid="activation-message-btn">
            <MessageCircle className="w-4 h-4" /> Mensagem de Ativação
          </Button>
          <Button onClick={() => setCustomOpen(!customOpen)} disabled={sending || !selected.length}
            size="sm" variant="outline" className="gap-2 mt-3" data-testid="custom-message-btn">
            <PenLine className="w-4 h-4" /> Mensagem Personalizada
          </Button>
          {sending && <Loader2 className="w-5 h-5 animate-spin text-slate-400 mt-4" />}
        </div>

        {customOpen && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="custom-message-box">
            <textarea value={customText} onChange={e => setCustomText(e.target.value)} rows={4}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="custom-message-input" />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-slate-500">Variáveis: <code>[nome do dono]</code> e <code>[nome da empresa]</code></p>
              <Button onClick={() => send('custom_message', customText)} disabled={sending || !customText.trim()}
                size="sm" className="bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] gap-2" data-testid="send-custom-btn">
                <Send className="w-4 h-4" /> Enviar para {selected.length} empresa(s)
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activation-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-3">
                    <input type="checkbox" checked={!!filtered.length && selected.length === filtered.length}
                      onChange={toggleAll} data-testid="select-all" />
                  </th>
                  <th className="text-left px-3 py-3">Empresa</th>
                  <th className="text-left px-3 py-3">Dono</th>
                  <th className="text-left px-3 py-3">WhatsApp</th>
                  <th className="text-left px-3 py-3">Plano</th>
                  <th className="text-center px-3 py-3">Dias</th>
                  <th className="text-center px-3 py-3">Avaliações</th>
                  <th className="text-center px-3 py-3">Status</th>
                  <th className="text-center px-3 py-3">Última avaliação</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(c => (
                  <Fragment key={c.id}>
                    <tr className="hover:bg-slate-50" data-testid={`activation-row-${c.id}`}>
                      <td className="px-3 py-2.5 text-center">
                        <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)}
                          data-testid={`select-${c.id}`} />
                      </td>
                      <td className="px-3 py-2.5 font-medium text-slate-800">{c.name}</td>
                      <td className="px-3 py-2.5 text-slate-600">{c.owner_name || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600">{fmtPhone(c.whatsapp)}</td>
                      <td className="px-3 py-2.5 text-slate-600">{c.plan_name}</td>
                      <td className="px-3 py-2.5 text-center text-slate-500">{c.days_since}</td>
                      <td className="px-3 py-2.5 text-center font-bold text-[#0A2647]">{c.total_feedbacks}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS[c.activation].style}`}>
                          {STATUS[c.activation].label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center text-slate-500">{fmtDate(c.last_feedback_at)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <button onClick={() => openLogs(c.id)} className="p-1 rounded hover:bg-slate-100"
                          title="Histórico de ações" data-testid={`logs-toggle-${c.id}`}>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded === c.id ? 'rotate-180' : ''}`} />
                        </button>
                      </td>
                    </tr>
                    {expanded === c.id && (
                      <tr className="bg-slate-50">
                        <td colSpan={10} className="px-6 py-3" data-testid={`logs-${c.id}`}>
                          {(logs[c.id] || []).length ? (
                            <ul className="space-y-1.5">
                              {logs[c.id].map(l => (
                                <li key={l.id} className="text-xs border-l-2 border-[#C5A059] pl-3">
                                  <span className="text-slate-400">{new Date(l.created_at).toLocaleString('pt-BR')} · {l.action_type}</span>
                                  <p className="text-slate-600">{l.message_text}</p>
                                </li>
                              ))}
                            </ul>
                          ) : <p className="text-xs text-slate-400">Nenhuma ação enviada para esta empresa.</p>}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {!filtered.length && <p className="py-12 text-center text-sm text-slate-500">Nenhuma empresa com esses filtros.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivationTab;
